import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { 
  Plus, TrendingDown, Wallet, CreditCard, Building2,
  Receipt, RefreshCw, Edit, Trash2, ShoppingCart, Users,
  FileText, DollarSign, ChevronDown, ChevronUp, Database
} from 'lucide-react';

const GIDER_KATEGORILERI = [
  "Kira", "Yakıt", "Elektrik", "Su", "Doğalgaz", "Telefon", "İnternet",
  "Maaş", "SGK", "Vergi", "Sigorta", "Bakım/Onarım", "Kırtasiye", 
  "Temizlik", "Güvenlik", "Reklam", "Taşıma/Nakliye", "Yemek", "Diğer"
];

const HAREKET_TIPLERI = [
  { value: "nakit_satis", label: "Nakit Satış", shortLabel: "N.Satış", icon: ShoppingCart, bg: "bg-green-500", lightBg: "bg-green-50", text: "text-green-700", isGiris: true },
  { value: "nakit_cari", label: "Nakit Cari", shortLabel: "N.Cari", icon: Users, bg: "bg-green-600", lightBg: "bg-green-50", text: "text-green-700", isGiris: true },
  { value: "kkarti_satis", label: "K.Kartı Satış", shortLabel: "K.Satış", icon: CreditCard, bg: "bg-blue-500", lightBg: "bg-blue-50", text: "text-blue-700", isGiris: true },
  { value: "kkarti_cari", label: "K.Kartı Cari", shortLabel: "K.Cari", icon: CreditCard, bg: "bg-blue-600", lightBg: "bg-blue-50", text: "text-blue-700", isGiris: true },
  { value: "havale_satis", label: "Havale Satış", shortLabel: "H.Satış", icon: Building2, bg: "bg-purple-500", lightBg: "bg-purple-50", text: "text-purple-700", isGiris: true },
  { value: "havale_cari", label: "Havale Cari", shortLabel: "H.Cari", icon: Building2, bg: "bg-purple-600", lightBg: "bg-purple-50", text: "text-purple-700", isGiris: true },
  { value: "cek_senet", label: "Çek/Senet", shortLabel: "Çek/Snt", icon: Receipt, bg: "bg-orange-500", lightBg: "bg-orange-50", text: "text-orange-700", isGiris: true },
  { value: "genel_gider", label: "Genel Gider", shortLabel: "Gider", icon: TrendingDown, bg: "bg-red-500", lightBg: "bg-red-50", text: "text-red-700", isGiris: false },
  { value: "diger", label: "Diğer", shortLabel: "Diğer", icon: FileText, bg: "bg-gray-500", lightBg: "bg-gray-50", text: "text-gray-700", isGiris: null }
];

const BANKA_LISTESI = [
  "Garanti BBVA", "İş Bankası", "Yapı Kredi", "Akbank", "Ziraat Bankası",
  "Halkbank", "Vakıfbank", "QNB Finansbank", "Denizbank", "TEB", "ING Bank", "Diğer"
];

export default function GiderGelir() {
  const { currentUser } = useApp();
  const [sirket, setSirket] = useState("odak_karabaglar");
  const [hareketler, setHareketler] = useState([]);
  const [kasaDurumu, setKasaDurumu] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingHareket, setEditingHareket] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  const [syncing, setSyncing] = useState(false);
  
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);
  
  const [formData, setFormData] = useState({
    tarih: today,
    islemTuru: 'giris',
    hareketTipi: 'nakit_satis',
    kategori: '',
    aciklama: '',
    tutar: '',
    bankaAdi: '',
    kartNo: '',
    cekNo: '',
    cekVadesi: '',
    cariAdi: '',
    faturaNo: '',
    irsaliyeNo: ''
  });

  useEffect(() => {
    loadData();
  }, [sirket, selectedDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { sirket, baslangic: selectedDate, bitis: selectedDate };
      const hRes = await api.getKasaHareketleri(params);
      setHareketler(hRes.data.hareketler || []);
      
      const kRes = await api.getKasaDurumu(sirket, selectedDate);
      setKasaDurumu(kRes.data);
    } catch (error) {
      toast.error('Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncNetsis = async () => {
    setSyncing(true);
    try {
      const res = await api.syncKasaFromNetsis({ sirket, baslangic: selectedDate, bitis: selectedDate });
      toast.success(`Netsis sync tamamlandı: ${res.data.eklenen} yeni, ${res.data.guncellenen} güncellendi`);
      loadData();
    } catch (error) {
      toast.error('Netsis sync başarısız: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.aciklama || !formData.tutar) {
      toast.error('Açıklama ve tutar zorunludur');
      return;
    }
    
    const tipInfo = HAREKET_TIPLERI.find(t => t.value === formData.hareketTipi);
    const islemTuru = tipInfo?.isGiris === false ? 'cikis' : (tipInfo?.isGiris === true ? 'giris' : formData.islemTuru);
    
    let odemeYontemi = 'nakit';
    if (formData.hareketTipi.includes('kkarti')) odemeYontemi = 'kredi_karti';
    else if (formData.hareketTipi.includes('havale')) odemeYontemi = 'havale';
    else if (formData.hareketTipi === 'cek_senet') odemeYontemi = 'cek';
    
    try {
      if (editingHareket) {
        await api.updateKasaHareketi(editingHareket.id, currentUser?.id, {
          ...formData, tutar: parseFloat(formData.tutar), islemTuru, odemeYontemi
        });
        toast.success('Güncellendi');
      } else {
        await api.createKasaHareketi({
          sirket, ...formData, tutar: parseFloat(formData.tutar), islemTuru, odemeYontemi,
          girenKullaniciId: currentUser?.id
        });
        toast.success('Kaydedildi');
      }
      setShowForm(false);
      setEditingHareket(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleEdit = (hareket) => {
    setEditingHareket(hareket);
    setFormData({
      tarih: hareket.tarih, islemTuru: hareket.islemTuru, hareketTipi: hareket.hareketTipi,
      kategori: hareket.kategori || '', aciklama: hareket.aciklama, tutar: hareket.tutar.toString(),
      bankaAdi: hareket.bankaAdi || '', kartNo: hareket.kartNo || '', cekNo: hareket.cekNo || '',
      cekVadesi: hareket.cekVadesi || '', cariAdi: hareket.cariAdi || '', faturaNo: hareket.faturaNo || '',
      irsaliyeNo: hareket.irsaliyeNo || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Silmek istediğinize emin misiniz?')) return;
    try {
      await api.deleteKasaHareketi(id, currentUser?.id);
      toast.success('Silindi');
      loadData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const resetForm = () => {
    setFormData({
      tarih: selectedDate, islemTuru: 'giris', hareketTipi: 'nakit_satis', kategori: '',
      aciklama: '', tutar: '', bankaAdi: '', kartNo: '', cekNo: '', cekVadesi: '',
      cariAdi: '', faturaNo: '', irsaliyeNo: ''
    });
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('tr-TR').format(amount || 0);

  // Kategoriye göre grupla
  const groupedHareketler = HAREKET_TIPLERI.reduce((acc, tip) => {
    acc[tip.value] = hareketler.filter(h => h.hareketTipi === tip.value);
    return acc;
  }, {});

  const getCategoryTotal = (tipValue) => {
    const items = groupedHareketler[tipValue] || [];
    return items.reduce((sum, h) => sum + (h.islemTuru === 'giris' ? h.tutar : -h.tutar), 0);
  };

  const gunlukToplam = kasaDurumu?.gunlukToplam || 0;

  return (
    <div className="space-y-4" data-testid="gunluk-kasa-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-textPrimary flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-primary" />
            Günlük Kasa
          </h1>
          <p className="text-xs text-textSecondary">
            {sirket === 'odak_karabaglar' ? 'Karabağlar' : '6.Site'} • {selectedDate}
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => { setShowForm(true); setEditingHareket(null); resetForm(); }} className="bg-primary">
            <Plus className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={handleSyncNetsis} disabled={syncing}>
            <Database className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" variant="outline" onClick={loadData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Filtreler */}
      <div className="flex gap-2 items-center">
        <Select value={sirket} onValueChange={setSirket}>
          <SelectTrigger className="w-40 h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="odak_karabaglar">Karabağlar</SelectItem>
            <SelectItem value="odak_6site">6.Site</SelectItem>
          </SelectContent>
        </Select>
        
        <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-36 h-8 text-sm" />
        
        <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => {
          const d = new Date(selectedDate); d.setDate(d.getDate() - 1);
          setSelectedDate(d.toISOString().split('T')[0]);
        }}>←</Button>
        <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setSelectedDate(today)}>Bugün</Button>
        <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => {
          const d = new Date(selectedDate); d.setDate(d.getDate() + 1);
          setSelectedDate(d.toISOString().split('T')[0]);
        }}>→</Button>
      </div>

      {/* Kategori Kartları */}
      {loading ? (
        <div className="text-center py-8 text-textSecondary">Yükleniyor...</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {HAREKET_TIPLERI.map(tip => {
            const items = groupedHareketler[tip.value] || [];
            const total = getCategoryTotal(tip.value);
            const Icon = tip.icon;
            const isExpanded = expandedCard === tip.value;
            
            return (
              <div key={tip.value} className={`rounded-lg border overflow-hidden ${tip.lightBg}`}>
                {/* Kart Başlığı */}
                <div 
                  className={`${tip.bg} text-white p-2 cursor-pointer flex items-center justify-between`}
                  onClick={() => setExpandedCard(isExpanded ? null : tip.value)}
                >
                  <div className="flex items-center gap-1">
                    <Icon className="h-4 w-4" />
                    <span className="text-xs font-medium">{tip.shortLabel}</span>
                    {items.length > 0 && (
                      <span className="text-[10px] bg-white/20 px-1 rounded">{items.length}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-bold">₺{formatCurrency(Math.abs(total))}</span>
                    {items.length > 0 && (isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                  </div>
                </div>
                
                {/* Detay Listesi */}
                {isExpanded && items.length > 0 && (
                  <div className="max-h-48 overflow-y-auto">
                    {items.map(h => (
                      <div key={h.id} className="p-2 border-b border-white/50 text-xs">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium ${tip.text} truncate`}>{h.aciklama}</p>
                            {h.cariAdi && <p className="text-gray-500 truncate">{h.cariAdi}</p>}
                            <p className="text-gray-400">#{h.fisNo}</p>
                          </div>
                          <div className="flex items-center gap-1 ml-2">
                            <span className={`font-bold ${h.islemTuru === 'giris' ? 'text-green-600' : 'text-red-600'}`}>
                              ₺{formatCurrency(h.tutar)}
                            </span>
                            <button onClick={(e) => { e.stopPropagation(); handleEdit(h); }} className="p-1 hover:bg-white/50 rounded">
                              <Edit className="h-3 w-3 text-blue-600" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleDelete(h.id); }} className="p-1 hover:bg-white/50 rounded">
                              <Trash2 className="h-3 w-3 text-red-600" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Boş mesajı */}
                {isExpanded && items.length === 0 && (
                  <div className="p-3 text-center text-xs text-gray-400">Kayıt yok</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Günlük Toplam */}
      <div className={`rounded-lg p-3 flex items-center justify-between ${gunlukToplam >= 0 ? 'bg-green-600' : 'bg-red-600'} text-white`}>
        <span className="font-bold">GÜNLÜK TOPLAM</span>
        <span className="text-xl font-bold">₺{formatCurrency(gunlukToplam)}</span>
      </div>

      {/* Yeni Kayıt Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-3 border-b border-border">
              <h2 className="font-bold text-textPrimary">
                {editingHareket ? `#${editingHareket.fisNo}` : 'Yeni Kayıt'}
              </h2>
              <button onClick={() => { setShowForm(false); setEditingHareket(null); }} className="text-xl">&times;</button>
            </div>
            
            <div className="p-3 space-y-3 max-h-[70vh] overflow-y-auto">
              {/* Hareket Tipi */}
              <div>
                <label className="text-xs font-medium text-textSecondary mb-1 block">Tür</label>
                <div className="grid grid-cols-3 gap-1">
                  {HAREKET_TIPLERI.slice(0, 9).map(tip => {
                    const Icon = tip.icon;
                    return (
                      <button
                        key={tip.value}
                        onClick={() => setFormData(p => ({ ...p, hareketTipi: tip.value }))}
                        className={`p-1.5 rounded text-xs flex flex-col items-center ${
                          formData.hareketTipi === tip.value ? `${tip.bg} text-white` : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        <Icon className="h-3 w-3 mb-0.5" />
                        <span className="truncate w-full text-center text-[10px]">{tip.shortLabel}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tarih ve Tutar */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-textSecondary mb-1 block">Tarih</label>
                  <Input type="date" value={formData.tarih} onChange={(e) => setFormData(p => ({ ...p, tarih: e.target.value }))} className="h-8 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-textSecondary mb-1 block">Tutar *</label>
                  <Input type="number" value={formData.tutar} onChange={(e) => setFormData(p => ({ ...p, tutar: e.target.value }))} placeholder="0" className="h-8 text-sm" />
                </div>
              </div>

              {/* Açıklama */}
              <div>
                <label className="text-xs font-medium text-textSecondary mb-1 block">Açıklama *</label>
                <Input value={formData.aciklama} onChange={(e) => setFormData(p => ({ ...p, aciklama: e.target.value }))} placeholder="Açıklama" className="h-8 text-sm" />
              </div>

              {/* Cari */}
              {['nakit_cari', 'kkarti_cari', 'havale_cari'].includes(formData.hareketTipi) && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-textSecondary mb-1 block">Cari</label>
                    <Input value={formData.cariAdi} onChange={(e) => setFormData(p => ({ ...p, cariAdi: e.target.value }))} className="h-8 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-textSecondary mb-1 block">Fatura No</label>
                    <Input value={formData.faturaNo} onChange={(e) => setFormData(p => ({ ...p, faturaNo: e.target.value }))} className="h-8 text-sm" />
                  </div>
                </div>
              )}

              {/* Banka */}
              {['kkarti_satis', 'kkarti_cari', 'havale_satis', 'havale_cari'].includes(formData.hareketTipi) && (
                <div>
                  <label className="text-xs font-medium text-textSecondary mb-1 block">Banka</label>
                  <Select value={formData.bankaAdi} onValueChange={(v) => setFormData(p => ({ ...p, bankaAdi: v }))}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent>
                      {BANKA_LISTESI.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Çek/Senet */}
              {formData.hareketTipi === 'cek_senet' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-textSecondary mb-1 block">No</label>
                    <Input value={formData.cekNo} onChange={(e) => setFormData(p => ({ ...p, cekNo: e.target.value }))} className="h-8 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-textSecondary mb-1 block">Vade</label>
                    <Input type="date" value={formData.cekVadesi} onChange={(e) => setFormData(p => ({ ...p, cekVadesi: e.target.value }))} className="h-8 text-sm" />
                  </div>
                </div>
              )}

              {/* Gider Kategorisi */}
              {formData.hareketTipi === 'genel_gider' && (
                <div>
                  <label className="text-xs font-medium text-textSecondary mb-1 block">Kategori</label>
                  <Select value={formData.kategori} onValueChange={(v) => setFormData(p => ({ ...p, kategori: v }))}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent>
                      {GIDER_KATEGORILERI.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            <div className="flex gap-2 p-3 border-t border-border">
              <Button variant="outline" onClick={() => { setShowForm(false); setEditingHareket(null); }} className="flex-1 h-8 text-sm">İptal</Button>
              <Button onClick={handleSubmit} className="flex-1 h-8 text-sm bg-primary">{editingHareket ? 'Güncelle' : 'Kaydet'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
